import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Form,
  FormFeedback,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap";
import ParticlesAuth from "../AuthenticationInner/ParticlesAuth";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { getBrandLogos } from "../../helpers/brandPortal";
import { persistTenantSession } from "../../helpers/api_helper";
import {
  MFA_RESEND_COOLDOWN_SEC,
  postLoginMfa,
  postLoginMfaResend,
  storeDeviceTrustToken,
} from "../../helpers/mfa_helper";
import { loginSuccess } from "../../slices/auth/login/reducer";

const MFA_TOKEN_KEY = "mfa_challenge_token";
const MFA_EXPIRES_KEY = "mfa_challenge_expires_at";
const MFA_METHOD_KEY = "mfa_challenge_method";
const MFA_EMAIL_HINT_KEY = "mfa_challenge_email_hint";
const MFA_RESEND_AT_KEY = "mfa_resend_available_at";

function clearMfaChallenge() {
  sessionStorage.removeItem(MFA_TOKEN_KEY);
  sessionStorage.removeItem(MFA_EXPIRES_KEY);
  sessionStorage.removeItem(MFA_METHOD_KEY);
  sessionStorage.removeItem(MFA_EMAIL_HINT_KEY);
  sessionStorage.removeItem(MFA_RESEND_AT_KEY);
}

/** Extrai segundos do message de MFA_RESEND_COOLDOWN (LAC-187). */
function cooldownSecondsFromMessage(message: string | undefined): number {
  const match = String(message || "").match(/(\d+)/);
  if (!match) return MFA_RESEND_COOLDOWN_SEC;
  return Math.max(1, Math.min(120, Number(match[1]) || MFA_RESEND_COOLDOWN_SEC));
}

/**
 * 2º fator após senha OK — TOTP ou e-mail + device trust (DOC 06 § 5.1.1).
 */
const LoginMfa = () => {
  const { t } = useTranslation();
  const logos = getBrandLogos();
  const navigate = useNavigate();
  const dispatch: any = useDispatch();
  const [loader, setLoader] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resendOk, setResendOk] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [rememberDevice, setRememberDevice] = useState(true);

  const mfaToken =
    typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem(MFA_TOKEN_KEY)
      : null;

  const mfaMethod =
    typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem(MFA_METHOD_KEY)
      : null;

  const emailHint =
    typeof sessionStorage !== "undefined"
      ? sessionStorage.getItem(MFA_EMAIL_HINT_KEY)
      : null;

  const isEmail = mfaMethod === "email";

  const expiresAt = useMemo(() => {
    if (typeof sessionStorage === "undefined") return null;
    const raw = sessionStorage.getItem(MFA_EXPIRES_KEY);
    if (!raw) return null;
    const n = Number(raw);
    return Number.isFinite(n) ? n : null;
  }, [mfaToken]);

  useEffect(() => {
    document.title = t("auth.loginMfa.documentTitle");
  }, [t]);

  // Ao abrir a tela (e-mail), inicia cooldown do reenvio — o login já disparou o OTP.
  useEffect(() => {
    if (!isEmail || typeof sessionStorage === "undefined") return;
    if (!sessionStorage.getItem(MFA_RESEND_AT_KEY)) {
      sessionStorage.setItem(
        MFA_RESEND_AT_KEY,
        String(Date.now() + MFA_RESEND_COOLDOWN_SEC * 1000)
      );
    }
  }, [isEmail, mfaToken]);

  useEffect(() => {
    if (!expiresAt) {
      setSecondsLeft(null);
      return;
    }
    const tick = () => {
      const left = Math.max(0, Math.ceil((expiresAt - Date.now()) / 1000));
      setSecondsLeft(left);
      if (left <= 0) {
        clearMfaChallenge();
      }
      const raw = sessionStorage.getItem(MFA_RESEND_AT_KEY);
      if (raw) {
        setResendCooldown(Math.max(0, Math.ceil((Number(raw) - Date.now()) / 1000)));
      } else {
        setResendCooldown(0);
      }
    };
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [expiresAt]);

  const validation = useFormik({
    enableReinitialize: true,
    initialValues: { code: "" },
    validationSchema: Yup.object({
      code: Yup.string().required(t("auth.loginMfa.validation.codeRequired")),
    }),
    onSubmit: async (values) => {
      if (!mfaToken) return;
      setLoader(true);
      setError(null);
      try {
        const envelope: any = await postLoginMfa({
          mfa_token: mfaToken,
          code: values.code.trim(),
          remember_device: rememberDevice,
        });
        if (!envelope?.success || !envelope?.data?.access_token) {
          setError(
            envelope?.error?.message || t("auth.loginMfa.invalidCode")
          );
          setLoader(false);
          return;
        }
        if (envelope.data.device_trust_token) {
          storeDeviceTrustToken(envelope.data.device_trust_token);
        } else if (!rememberDevice) {
          storeDeviceTrustToken(null);
        }
        clearMfaChallenge();
        persistTenantSession(envelope.data);
        dispatch(loginSuccess(envelope.data));
        navigate("/dashboard", { replace: true });
      } catch (e: any) {
        setError(e?.message || t("auth.loginMfa.invalidCode"));
        setLoader(false);
      }
    },
  });

  const onResend = async () => {
    if (!mfaToken || !isEmail || resendCooldown > 0) return;
    setResending(true);
    setError(null);
    setResendOk(false);
    try {
      const envelope: any = await postLoginMfaResend(mfaToken);
      if (!envelope?.success) {
        setError(envelope?.error?.message || t("auth.loginMfa.resendError"));
        return;
      }
      sessionStorage.setItem(
        MFA_RESEND_AT_KEY,
        String(Date.now() + MFA_RESEND_COOLDOWN_SEC * 1000)
      );
      setResendCooldown(MFA_RESEND_COOLDOWN_SEC);
      setResendOk(true);
    } catch (e: any) {
      const secs = cooldownSecondsFromMessage(e?.message);
      sessionStorage.setItem(
        MFA_RESEND_AT_KEY,
        String(Date.now() + secs * 1000)
      );
      setResendCooldown(secs);
      setError(e?.message || t("auth.loginMfa.resendError"));
    } finally {
      setResending(false);
    }
  };

  if (!mfaToken || (secondsLeft !== null && secondsLeft <= 0)) {
    return <Navigate to="/login" replace />;
  }

  const mm = secondsLeft != null ? Math.floor(secondsLeft / 60) : null;
  const ss =
    secondsLeft != null
      ? String(secondsLeft % 60).padStart(2, "0")
      : null;

  return (
    <React.Fragment>
      <ParticlesAuth>
        <div className="auth-page-content mt-lg-5">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="text-center mt-sm-5 mb-4 text-white-50">
                  <div>
                    <Link to="/login" className="d-inline-block auth-logo">
                      <img src={logos.light} alt={logos.alt} height="20" />
                    </Link>
                  </div>
                </div>
              </Col>
            </Row>
            <Row className="justify-content-center">
              <Col md={8} lg={6} xl={5}>
                <Card className="mt-4">
                  <CardBody className="p-4">
                    <div className="text-center mt-2">
                      <h5 className="text-primary">{t("auth.loginMfa.title")}</h5>
                      <p className="text-muted">
                        {isEmail
                          ? t("auth.loginMfa.subtitleEmail", {
                              email: emailHint || "",
                            })
                          : t("auth.loginMfa.subtitle")}
                      </p>
                      {mm != null && ss != null ? (
                        <p className="small text-muted mb-0">
                          {t("auth.loginMfa.expiresIn", {
                            time: `${mm}:${ss}`,
                          })}
                        </p>
                      ) : null}
                    </div>
                    {error ? (
                      <Alert color="danger" className="mt-3 mb-0">
                        {error}
                      </Alert>
                    ) : null}
                    {resendOk && !error ? (
                      <Alert color="success" className="mt-3 mb-0">
                        {t("auth.loginMfa.resendOk")}
                      </Alert>
                    ) : null}
                    <div className="p-2 mt-3">
                      <Form
                        onSubmit={(e) => {
                          e.preventDefault();
                          validation.handleSubmit();
                        }}
                      >
                        <div className="mb-3">
                          <Label htmlFor="mfa-code">{t("auth.loginMfa.code")}</Label>
                          <Input
                            id="mfa-code"
                            name="code"
                            type="text"
                            autoComplete="one-time-code"
                            inputMode="numeric"
                            autoFocus
                            value={validation.values.code}
                            onChange={validation.handleChange}
                            onBlur={validation.handleBlur}
                            invalid={
                              !!(
                                validation.touched.code &&
                                validation.errors.code
                              )
                            }
                            placeholder={t("auth.loginMfa.codePlaceholder")}
                          />
                          {validation.touched.code && validation.errors.code ? (
                            <FormFeedback type="invalid">
                              {String(validation.errors.code)}
                            </FormFeedback>
                          ) : null}
                          <div className="form-text">
                            {isEmail
                              ? t("auth.loginMfa.codeHintEmail")
                              : t("auth.loginMfa.codeHint")}
                          </div>
                        </div>
                        <div className="form-check mb-3">
                          <Input
                            className="form-check-input"
                            type="checkbox"
                            id="mfa-remember-device"
                            checked={rememberDevice}
                            onChange={(e) => setRememberDevice(e.target.checked)}
                          />
                          <Label
                            className="form-check-label"
                            htmlFor="mfa-remember-device"
                          >
                            {t("auth.loginMfa.rememberDevice")}
                          </Label>
                        </div>
                        <div className="mt-4">
                          <Button
                            color="success"
                            className="w-100"
                            type="submit"
                            disabled={loader}
                          >
                            {loader ? (
                              <Spinner size="sm" className="me-2" />
                            ) : null}
                            {t("auth.loginMfa.submit")}
                          </Button>
                        </div>
                        {isEmail ? (
                          <div className="mt-3 text-center">
                            <Button
                              color="link"
                              type="button"
                              className="p-0"
                              disabled={resending || loader || resendCooldown > 0}
                              onClick={() => void onResend()}
                            >
                              {resending ? (
                                <Spinner size="sm" className="me-1" />
                              ) : null}
                              {resendCooldown > 0
                                ? t("auth.loginMfa.resendWait", {
                                    seconds: resendCooldown,
                                  })
                                : t("auth.loginMfa.resend")}
                            </Button>
                          </div>
                        ) : null}
                        <div className="mt-3 text-center">
                          <Link
                            to="/login"
                            className="fw-semibold text-primary text-decoration-underline"
                            onClick={() => clearMfaChallenge()}
                          >
                            {t("auth.loginMfa.backLogin")}
                          </Link>
                        </div>
                      </Form>
                    </div>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </ParticlesAuth>
    </React.Fragment>
  );
};

export default LoginMfa;
